import { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/common/Button/Button";
import { Link, useLocation } from "react-router-dom";

interface HeroProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tag?: {
    text: string;
    href: string;
  };
  title: ReactNode;
  description: ReactNode;
  cta?: {
    primary: {
      text: string;
      href: string;
    };
    secondary: {
      text: string;
      href: string;
    };
  };
}

export const Hero = ({
  tag,
  title,
  description,
  cta,
  className,
  ...props
}: HeroProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-background pt-16 md:pt-32",
        className
      )}
      {...props}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-light opacity-50" />

      {/* Left side background SVG */}
      {isHomePage && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[5%] hidden md:block">
          <img
            src="/subtle-bg1.svg"
            alt=""
            className="max-h-[100vh] w-auto opacity-70"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Right side background SVG */}
      {isHomePage && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-[5%] hidden md:block">
          <img
            src="/subtlebg2.svg"
            alt=""
            className="max-h-[100vh] w-auto opacity-70"
            aria-hidden="true"
          />
        </div>
      )}

      <div className="container relative mx-auto px-4">
        {/* Content */}
        <div className="mx-auto max-w-[800px] text-center">
          {/* Tag */}
           {tag && (
            <Link
              to={tag.href}
              className="inline-flex items-center rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100 mb-6"
            >
              <span className="relative">{tag.text}</span>
              <svg
                className="ml-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          )}

          {/* Title */}
          <h1 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            {title}
          </h1>

          {/* Description */}
          <p className="mb-8 text-lg text-gray-600 sm:text-xl max-w-3xl mx-auto">
            {description}
          </p>

          {/* CTAs */}
          {cta && (
            <div className="flex flex-row items-center justify-center gap-4 mb-12 md:mb-16">
              <Button
                variant="solid"
                color="primary"
                size="lg"
                onClick={() => window.open(cta.primary.href === '/signup' ? '/signup' : cta.primary.href, '_blank')}
              >
                {cta.primary.text}
              </Button>
              <Button
                variant="gradient"
                color="primary"
                size="lg"
                onClick={() => window.open(cta.secondary.text.toLowerCase().includes('demo') ? 'https://cal.com/cobalt/welcome-to-cobalt' : cta.secondary.href, '_blank')}
              >
                {cta.secondary.text}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
